"use client";

import { run } from "@/lib/action-run";
import { useEffect, useState, useTransition } from "react";
import { ArrowLeftRight, Calculator, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CURRENCIES, formatMoney } from "@/lib/money";
import { formatAmount, tryParseAmountInput } from "@/lib/currency";
import {
  convertCurrencyMinor,
  loadCachedFxRate,
  saveCachedFxRate,
  type CachedFxRate,
} from "@/lib/currency-calc";
import { fetchFxRateAction } from "@/app/(app)/trips/[tripId]/budget/budget-actions";

const OTHER = "OTHER";

export function CurrencyCalculatorDialog({
  baseCurrency = "KRW",
  initialFromCurrency,
  trigger,
}: {
  baseCurrency?: string;
  initialFromCurrency?: string;
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);


  // Source (From) currency
  const defaultFrom =
    initialFromCurrency && initialFromCurrency.toUpperCase() !== baseCurrency.toUpperCase()
      ? initialFromCurrency.toUpperCase()
      : baseCurrency === "KRW"
        ? "USD"
        : "KRW";

  const isPresetFrom = (CURRENCIES as readonly string[]).includes(defaultFrom);
  const [fromSel, setFromSel] = useState<string>(isPresetFrom ? defaultFrom : OTHER);
  const [fromCustom, setFromCustom] = useState(isPresetFrom ? "" : defaultFrom);

  // Target (To) currency
  const isPresetTo = (CURRENCIES as readonly string[]).includes(baseCurrency.toUpperCase());
  const [toSel, setToSel] = useState<string>(isPresetTo ? baseCurrency.toUpperCase() : OTHER);
  const [toCustom, setToCustom] = useState(isPresetTo ? "" : baseCurrency.toUpperCase());

  // Amounts
  const [fromAmount, setFromAmount] = useState<string>("100");
  const [customRate, setCustomRate] = useState<string>("");
  const [rateInfo, setRateInfo] = useState<CachedFxRate | null>(null);
  const [isFetching, startFetchTransition] = useTransition();

  const fromCurrency = (fromSel === OTHER ? fromCustom.trim().toUpperCase() : fromSel) || "";
  const toCurrency = (toSel === OTHER ? toCustom.trim().toUpperCase() : toSel) || "";

  // Load rate when currencies change or dialog opens
  useEffect(() => {
    if (!open) return;
    if (!fromCurrency || !toCurrency) return;

    if (fromCurrency === toCurrency) {
      setRateInfo({ rate: 1, timestamp: Date.now() });
      setCustomRate("1");
      return;
    }

    // Try cached rate first
    const cached = loadCachedFxRate(fromCurrency, toCurrency);
    if (cached) {
      setRateInfo(cached);
      setCustomRate(String(cached.rate));
    } else {
      setRateInfo(null);
      setCustomRate("");
    }

    // Fetch fresh rate if online
    if (navigator.onLine) {
      startFetchTransition(async () => {
        const result = await run(fetchFxRateAction(fromCurrency, toCurrency));
        if (result.ok) {
          saveCachedFxRate(fromCurrency, toCurrency, result.rate);
          setRateInfo({ rate: result.rate, timestamp: Date.now() });
          setCustomRate(String(result.rate));
        } else if (!cached) {
          toast.error("환율을 불러오지 못했습니다. 환율을 직접 입력해주세요.");
        }
      });
    }
  }, [open, fromCurrency, toCurrency]);

  function handleRefreshRate() {
    if (!fromCurrency || !toCurrency) return;
    if (fromCurrency === toCurrency) {
      setRateInfo({ rate: 1, timestamp: Date.now() });
      setCustomRate("1");
      return;
    }
    if (!navigator.onLine) {
      toast.error("오프라인 상태입니다. 저장된 환율을 사용하거나 직접 입력해주세요.");
      return;
    }

    startFetchTransition(async () => {
      const result = await run(fetchFxRateAction(fromCurrency, toCurrency));
      if (result.ok) {
        saveCachedFxRate(fromCurrency, toCurrency, result.rate);
        setRateInfo({ rate: result.rate, timestamp: Date.now() });
        setCustomRate(String(result.rate));
        toast.success("최신 환율을 가져왔어요");
      } else {
        toast.error("환율 조회에 실패했습니다. 직접 입력해주세요.");
      }
    });
  }

  function handleSwap() {
    const prevFromSel = fromSel;
    const prevFromCustom = fromCustom;
    const prevToSel = toSel;
    const prevToCustom = toCustom;

    setFromSel(prevToSel);
    setFromCustom(prevToCustom);
    setToSel(prevFromSel);
    setToCustom(prevFromCustom);
  }

  // Calculation
  const activeRate = Number(customRate);
  const isRateValid = Number.isFinite(activeRate) && activeRate > 0;
  const fromMinor = fromCurrency ? tryParseAmountInput(fromAmount, fromCurrency) : null;

  let calculatedToMinor: number | null = null;
  let calculationError: string | null = null;

  if (fromAmount.trim() !== "" && fromMinor === null) {
    calculationError = "올바른 금액 형식이 아닙니다";
  } else if (fromMinor !== null && isRateValid && fromCurrency && toCurrency) {
    try {
      calculatedToMinor = convertCurrencyMinor(fromMinor, fromCurrency, toCurrency, activeRate);
    } catch {
      calculationError = "환율 계산 중 오류가 발생했습니다";
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-1.5">
            <Calculator className="size-4" />
            <span>환율 계산기</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="size-5 text-primary" />
            <span>실시간 환율 계산기</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Currencies & Swap */}
          <div className="flex items-center gap-2">
            {/* From currency */}
            <div className="flex-1 flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">보내는 통화</Label>
              <Select value={fromSel} onValueChange={setFromSel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER}>직접 입력</SelectItem>
                </SelectContent>
              </Select>
              {fromSel === OTHER && (
                <Input
                  maxLength={3}
                  placeholder="예: THB"
                  value={fromCustom}
                  onChange={(e) => setFromCustom(e.target.value.toUpperCase())}
                  className="mt-1"
                />
              )}
            </div>

            {/* Swap button */}
            <div className="pt-5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleSwap}
                aria-label="통화 서로 바꾸기"
                title="통화 바꾸기"
              >
                <ArrowLeftRight className="size-4" />
              </Button>
            </div>

            {/* To currency */}
            <div className="flex-1 flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">받는 통화</Label>
              <Select value={toSel} onValueChange={setToSel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER}>직접 입력</SelectItem>
                </SelectContent>
              </Select>
              {toSel === OTHER && (
                <Input
                  maxLength={3}
                  placeholder="예: USD"
                  value={toCustom}
                  onChange={(e) => setToCustom(e.target.value.toUpperCase())}
                  className="mt-1"
                />
              )}
            </div>
          </div>

          {/* Amount input & result */}
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="calcFromAmount" className="text-xs">
                금액 ({fromCurrency || "통화 선택"})
              </Label>
              <Input
                id="calcFromAmount"
                type="text"
                inputMode="decimal"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="금액을 입력하세요"
                className="text-base font-medium"
              />
              {calculationError && (
                <p className="text-xs text-destructive">{calculationError}</p>
              )}
            </div>

            <div className="border-t pt-2">
              <span className="text-xs text-muted-foreground">환산 결과:</span>
              <div className="mt-1 text-xl font-bold tracking-tight text-foreground">
                {calculatedToMinor !== null && toCurrency
                  ? formatMoney(calculatedToMinor, toCurrency)
                  : "-"}
              </div>
            </div>
          </div>

          {/* Rate status & override */}
          <div className="flex flex-col gap-2 rounded-lg border p-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                적용 환율 (1 {fromCurrency || "?"} = {toCurrency || "?"})
              </span>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={isFetching || !fromCurrency || !toCurrency}
                onClick={handleRefreshRate}
                className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <RefreshCw className={`size-3 ${isFetching ? "animate-spin" : ""}`} />
                <span>새로고침</span>
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
                placeholder="환율 직접 입력"
                className="h-8 text-xs"
              />
            </div>

            {rateInfo?.timestamp ? (
              <p className="text-[11px] text-muted-foreground">
                기준 시각: {format(new Date(rateInfo.timestamp), "yyyy.MM.dd HH:mm", { locale: ko })}
                {!navigator.onLine && " (오프라인 캐시)"}
              </p>
            ) : (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                조회된 환율이 없습니다. 환율을 직접 입력할 수 있습니다.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
